import { useMutation, useQuery } from '@tanstack/react-query';
import { IInvoice, IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { customFetcher } from '../useFetch';
import { IDocument } from '../../Interfaces/IDocument';
import { IinvoiceState } from '../../Interfaces/IInvoiceState';
import { IInvoicePaid } from '../../Interfaces/IInvoicePaid';
import { ILegalTransactionRecordNumber } from '../../Interfaces/ILegalTransactionRecordNumber';

export const useCreateBulkInvoices = () => {
  const {
    mutateAsync: createBulkInvoices,
    isPending: isPendingCreateBulkInvoices,
  } = useMutation({
    mutationFn: (invArray: IInvoiceFullObject[]) =>
      customFetcher({
        endpoint: '/invoice/bulk',
        method: 'POST',
        body: invArray,
      }),
  });

  return {
    createBulkInvoices,
    isPendingCreateBulkInvoices,
  };
};

export const useDeleteInvoice = () => {
  const { mutateAsync: deleteInvoice, isPending: isPendingDeleteInvoice } =
    useMutation({
      mutationFn: (idToDelte: number) =>
        customFetcher({
          endpoint: `/invoice/${idToDelte}`,
          method: 'DELETE',
        }),
    });

  return {
    deleteInvoice,
    isPendingDeleteInvoice,
  };
};

export interface IInvoiceClientScan {
  EPCString?: string | null;
  Total?: number | null;
  InvoiceNumber?: string;
  IBAN?: string;
  BIC?: string;
  Recipient?: string;
}
export type IDocumentWithScan = IDocument & {
  ClientScan?: IInvoiceClientScan;
};

export const useCheckDocumentForInvoice = () => {
  const {
    mutateAsync: checkDocumentForInvoice,
    isPending: isPendingCheckDocumentForInvoice,
  } = useMutation<IInvoiceFullObject[], Error, IDocumentWithScan[]>({
    mutationFn: (documentArray: IDocumentWithScan[]) =>
      customFetcher({
        endpoint: '/document/check_invoice',
        method: 'POST',
        body: documentArray,
      }),
  });

  return {
    checkDocumentForInvoice,
    isPendingCheckDocumentForInvoice,
  };
};

export const useUploadDocumentForInvoice = () => {
  const {
    mutateAsync: uploadeDocumentForInvoice,
    isPending: isPendingCheckDocumentForInvoice,
  } = useMutation<IInvoiceFullObject[], Error, IDocumentWithScan[]>({
    mutationFn: (documentArray: IDocumentWithScan[]) =>
      customFetcher({
        endpoint: '/invoice/check_invoice',
        method: 'POST',
        body: documentArray,
      }),
  });

  return {
    uploadeDocumentForInvoice,
    isPendingCheckDocumentForInvoice,
  };
};

export const useImportDatevCSv = () => {
  const { mutateAsync: importDatevCsv, isPending: isPendingImportDatevCsv } =
    useMutation<IInvoicePaid[], Error, IDocument>({
      mutationFn: (documentObject: IDocument) =>
        customFetcher({
          endpoint: '/invoice/import_datev_csv',
          method: 'POST',
          body: documentObject,
        }),
    });

  return {
    importDatevCsv,
    isPendingImportDatevCsv,
  };
};

export const useGetInvoices = () => {
  const {
    data: invoices,
    isLoading: isLoadingInvoices,
    refetch: refetchInvoices,
  } = useQuery<IInvoice[]>({
    queryKey: ['invoice'],
    queryFn: () => customFetcher({ endpoint: '/invoice' }),
  });

  return {
    invoices,
    isLoadingInvoices,
    refetchInvoices,
  };
};

//////////////////// LegalTransactionFullObject
export const useGetInvoiceFullObject = (idInvoice?: number) => {
  const {
    data: invoiceFullObject,
    isFetching: isFetchingInvoiceFullObject,
    refetch: refetchInvoiceFullObject,
  } = useQuery<IInvoiceFullObject>({
    refetchOnWindowFocus: false,
    queryKey: ['invoice/fullObject', idInvoice],
    queryFn: () =>
      customFetcher({
        endpoint: `/invoice/fullObject/${idInvoice}`,
      }),
    enabled: !!idInvoice,
  });

  return {
    invoiceFullObject,
    isFetchingInvoiceFullObject,
    refetchInvoiceFullObject,
  };
};

export const useGetInvoiceFullobjectById = () => {
  const {
    mutateAsync: getInvoiceFullobjectById,
    isPending: isPendingGetInvoiceFullobjectById,
  } = useMutation<IInvoiceFullObject, Error, number>({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/invoice/fullObject/${id}`,
      }),
  });

  return {
    getInvoiceFullobjectById,
    isPendingGetInvoiceFullobjectById,
  };
};

export const useUpdateInvoiceFullObject = () => {
  const {
    mutateAsync: updateInvoiceFullObject,
    isPending: isPendingUpdateInvoiceFullObject,
  } = useMutation({
    mutationFn: (currentObject: IInvoiceFullObject) =>
      customFetcher({
        endpoint: '/invoice/fullObject',
        method: 'PUT',
        body: currentObject,
      }),
  });

  return {
    updateInvoiceFullObject,
    isPendingUpdateInvoiceFullObject,
  };
};

export const useGetInvoicesStates = () => {
  const {
    data: invoiceStates,
    isLoading: isLoadinginvoiceStates,
    refetch: refetchinvoiceStates,
  } = useQuery<IinvoiceState[]>({
    queryKey: ['invoicestate'],
    queryFn: () => customFetcher({ endpoint: '/invoicestate' }),
  });

  return {
    invoiceStates,
    isLoadinginvoiceStates,
    refetchinvoiceStates,
  };
};

type UnassignedResponse = ILegalTransactionRecordNumber[];

export const useGetUnassignedRecordNumbers = (opts?: {
  idLegalTransaction?: number;
}) => {
  const idLt = opts?.idLegalTransaction;

  const {
    data: unassignedRecordNumbers,
    isLoading: isLoadingUnassignedRecordNumbers,
    refetch: refetchUnassignedRecordNumbers,
  } = useQuery<UnassignedResponse>({
    queryKey: ['invoice', 'unassigned_recordnumbers', idLt ?? 'all'],
    queryFn: () =>
      customFetcher({
        endpoint: idLt
          ? `/invoice/unassigned_recordnumbers?idLegalTransaction=${idLt}`
          : `/invoice/unassigned_recordnumbers`,
      }),
  });

  return {
    unassignedRecordNumbers,
    isLoadingUnassignedRecordNumbers,
    refetchUnassignedRecordNumbers,
  };
};
